import { BoxProps } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import ceil from "lodash/ceil";
import { useMemo } from "react";

import { PartWeightModal } from "#modules/quoteRequestLines/view/PartWeightModal";
import { Icon } from "#shared/icons/Icon";
import { Chip } from "#shared/view/designSystem/Chip";
import { getMaterialWeight, MaterialGradeType, ExtendedPart } from "shared";

type Props = {
  part: ExtendedPart;
  density: number;
  onSubmitPartWeight?: () => void;
  quoteRequestLineId: string;
  materialGrade: MaterialGradeType;
};

export const WeightChip = ({
  part,
  density,
  onSubmitPartWeight,
  quoteRequestLineId,
  materialGrade,
  ...props
}: BoxProps & Props) => {
  const [partWeightModalOpened, { open: openPartWeightModal, close: closePartWeightModal }] =
    useDisclosure(false);

  const canEditWeight = !!part.weight;

  const weight = useMemo(
    () => part.weight ?? ceil(getMaterialWeight(part.volume ?? 0, density), 3),
    [part.weight, part.volume, density],
  );

  const handleOpenPartWeightModal = (e: React.MouseEvent<HTMLInputElement>) => {
    if (!canEditWeight) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    openPartWeightModal();
  };

  if (!weight) {
    return null;
  }

  return (
    <>
      <Chip
        size="xs"
        icon={<Icon name="weight" size={14} />}
        checked
        color="violet.4"
        autoContrast
        onClick={handleOpenPartWeightModal}
        {...props}
      >
        {`${weight} kg`}
      </Chip>

      <PartWeightModal
        part={part}
        weight={weight}
        partWeightModalOpened={partWeightModalOpened}
        closePartWeightModal={closePartWeightModal}
        onSubmit={onSubmitPartWeight}
        quoteRequestLineId={quoteRequestLineId}
        materialGrade={materialGrade}
      />
    </>
  );
};
